import ngRedux from 'ng-redux'
import { VdTracking, VdFeatureManager } from '@vend/vend.js'
import { VdToastNotificationService } from '@vend/vend.ui'
import angular, { IScope } from 'angular'
import { ApiCustomer, ApiProduct } from '@vend/types'
import { wrToastNegative } from '../../components/react/helios/ToastNotifications/WrToastNotifications'

import { QuickKeysManager } from '../../managers/quick-keys-manager'
import { RegisterManager } from '../../managers/register-manager'
import { SaleManager } from '../../managers/sale-manager'
import { SearchManager } from '../../managers/search-manager'
import { SyncManager } from '../../managers/sync-manager'
import { $log, $rootScope } from '../../ng-compat/imports'
import {
  KLARNA_EXCHANGE_NOT_POSSIBLE_MESSAGE,
  shouldPreventExchangeForKlarna,
} from '../../functions/payment-types/klarna'
import { DebounceOptions, EVENT } from '../../constants'
import {
  LSPAY_ECOM_EXCHANGE_NOT_POSSIBLE_TOAST_KEY,
  isReturnedESale,
} from '../../functions/payment-types/lspayEcom'
import {
  ReduxCurrentSale,
  selectCurrentSale,
} from '../../state/slices/currentSale/currentSaleSlice'
import { RootState } from '../../state/store'

interface CustomerResult {
  customer: ApiCustomer
  exactMatch: boolean
  variantCount: number
}

interface ProductResult {
  barcodeData?: unknown
  exactMatch: boolean
  product: ApiProduct
  variantCount: number
}

/**
 * =======================================
 * WORKSPACE CONTROLLER
 * =======================================
 */
export class WorkspaceCtrl {
  public productErrorTemplate: string // All template are defined as string based on the declaration in assets/types/templates.d.ts
  public quickKeysEnabled: boolean = false
  public suggestionSource: $TSFixMe
  public syncing = true
  public debounceOptions: DebounceOptions | undefined
  public currentSale: ReduxCurrentSale

  constructor(
    private $scope: IScope,
    private productModals: $TSFixMe,
    private searchManager: SearchManager,
    private vdTracking: VdTracking,
    private vdFeatureManager: VdFeatureManager,
    private syncManager: SyncManager,
    private registerManager: RegisterManager,
    private quickKeysManager: QuickKeysManager,
    private vdToastNotificationService: VdToastNotificationService,
    private productSuggestions: $TSFixMe,
    private saleManager: SaleManager,
    private $ngRedux: ngRedux.INgRedux
  ) {
    'ngInject'
    function logAndNotifyError(
      message: $TSFixMe,
      ...additionalInfo: $TSFixMe[]
    ) {
      $log.warn(`[Workspace] ${message}`, ...additionalInfo)
      vdToastNotificationService.negative({ message })
    }
    this.currentSale = selectCurrentSale(this.$ngRedux.getState()) // Set default value for currentSale before connect callback function ran
    const disconectRedux = this.$ngRedux.connect((state: RootState) => ({
      currentSale: selectCurrentSale(state),
    }))(this)

    this.suggestionSource = this.productSuggestions.getSource({ global: true })
    this.debounceOptions = this.saleManager.debounceOptions
    // Replaces the default error message for the product search autocomplete
    this.productErrorTemplate = this.productSuggestions.productErrorTemplate
    this._activeRegisterChange()
    this.syncManager
      .whenCompleteDatasetAvailable()
      .then(() => this.searchManager.whenSyncComplete())
      .then(() => {
        this.syncing = false
        $rootScope.$emit('autocomplete:global-search:focus')
      })
    const unsubscribeQuickKeyEvents = $rootScope.$on(
      'vd-quick-keys:product-selected',
      (event, qkProductDetails) => {
        const suggestion = {
          id: qkProductDetails.productId,
          exactMatch: qkProductDetails.isChild,
        }
        const { label } = qkProductDetails
        const notFoundMsg = `"${label}" has been deleted and can't be sold. Please select another product.`
        this.productSuggestions
          .enhance(suggestion)
          .then((result: ProductResult) => {
            const { product } = result
            if (product && product.active) {
              // Mitigate against a performance bug where a retailer can select a product but transition to the sale
              // confirmation view before it is added to the sale.
              if (
                this.registerManager.isActiveRegisterOpen() &&
                this.saleManager.isInitialised
              ) {
                this.saleManager.saleCalculating = true
              }
              this.vdTracking.trackUserEvent({
                eventName: 'Quick Key Selected',
                metadata: {
                  'Has Variants': result.variantCount > 0,
                  'Exact Match': result.exactMatch,
                  'View Product Info': !this.registerManager.isActiveRegisterOpen(),
                },
              })
              this.onSelectResult(result)
            } else {
              let message
              if (!product) {
                message = notFoundMsg
              } else {
                message = `"${label}" has been deactivated and can't be sold. Please select another product.`
              }
              logAndNotifyError(message, result)
            }
          })
          .catch(() => logAndNotifyError(notFoundMsg))
      }
    )
    this.registerManager.on(
      'activeRegisterChange',
      this._activeRegisterChange,
      this
    )
    this.$scope.$on('$destroy', () => {
      unsubscribeQuickKeyEvents()
      disconectRedux()
      this.registerManager.off(
        'activeRegisterChange',
        this._activeRegisterChange
      )
    })
  }

  /**
   * @private
   * @method _doSelectVariant
   *
   * @param product {Object}
   *      The product the variant options belong to.
   *
   * @return {Promise<Object>} The selected variant option.
   */
  private _doSelectVariant(product: any) {
    $log.debug('[Global Search] Do select variant', product)
    const options = { parentProduct: product }
    return this.productModals.selectVariant(options)
  }

  /**
   * Selects the given product(s) to be added to the sale.
   *
   * @private
   * @method _selectProduct
   *
   * @param  product {Product|Array}
   *         The product to select (or array of products if multiple matches)
   */
  private _selectProduct(product: ApiProduct | ApiProduct[]) {
    if (angular.isArray(product)) {
      if (product.length !== 1) {
        // @todo some kind of prompt to the user about multiple matches
        $log.warn('[Global Search] Multiple product matches', product)
      }
      product = product[0]
    }
    $log.debug('[Global Search] select product', product)
    // Broadcast product selected event down to lower scope, with product that should be added as parameter.
    $rootScope.$emit(EVENT.SEARCH_PRODUCT_SELECTED, product)
  }

  /**
   * Handle selection of a customer result, emitting the `search:customer-selected` event so the customer can be
   * added to the sale.
   *
   * @private
   * @method _onSelectCustomerResult
   *
   * @param  customerResult {CustomerResult}
   *         The customer result selected
   */
  private _onSelectCustomerResult(customerResult: CustomerResult) {
    const customer = customerResult.customer
    $log.debug('[Global Search] Customer result selected', customerResult)
    $rootScope.$emit('search:customer-selected', customer)
  }

  /**
   * Handle selection of a product result, displaying the variant picker if necessary.
   *
   * @method onSelectProduct
   *
   * @param productResult {ProductResult}
   *        An object describing the product to be added to the sale.
   */
  public onSelectProduct(productResult: ProductResult) {
    if (this.currentSale && shouldPreventExchangeForKlarna(this.currentSale)) {
      this.saleManager.saleCalculating = false
      this.vdToastNotificationService.negative({
        message: KLARNA_EXCHANGE_NOT_POSSIBLE_MESSAGE,
      })
      return
    }
    if (
      this.currentSale &&
      this.vdFeatureManager.get('lspay_eseries_refunds_on_xseries') &&
      isReturnedESale(this.currentSale)
    ) {
      this.saleManager.saleCalculating = false
      wrToastNegative(LSPAY_ECOM_EXCHANGE_NOT_POSSIBLE_TOAST_KEY)
      this.vdTracking.trackUserEvent({
        eventName: 'Attempt to exchange an lspay eCom sale',
        metadata: {
          saleId: this.currentSale.localId,
        },
      })
      return
    }
    const product = productResult.product
    $log.debug('[Global Search] Product result selected', productResult)
    if (productResult.variantCount && !productResult.exactMatch) {
      $log.debug('[Global Search] Product result with variants selected')
      this._doSelectVariant(product)
        .then((selectedResult: $TSFixMe) =>
          this._selectProduct(selectedResult.variant)
        )
        .catch(() => {
          // If a variant product was not selected, reset the sale calculating state
          this.saleManager.saleCalculating = false
        })
    } else {
      this._selectProduct(product)
    }
  }

  /**
   * Handles a result being selected by the search by determining whether it is a customer or product result and
   * delegating to the appropriate handler.
   *
   * @method onSelectResult
   *
   * @param  result
   *         The selected search result
   */
  public onSelectResult(result: CustomerResult | ProductResult) {
    if ('barcodeData' in result) {
      $rootScope.$emit('search:embedded-barcode-selected', {
        product: result.product,
        barcodeData: result.barcodeData,
      })
    } else if ('product' in result) {
      this.onSelectProduct(result)
    } else if (result.customer) {
      this._onSelectCustomerResult(result)
    }
  }

  public focusProductSearch() {
    $rootScope.$emit('autocomplete:global-search:focus', {
      isFocusable: true,
    })
  }

  /**
   * Triggered when the active register is updated
   *
   * @private
   * @method _activeRegisterChange
   *
   */
  private _activeRegisterChange() {
    this.quickKeysEnabled = this.quickKeysManager.isQuickKeysEnabled()
  }
}

/**
 * Provides a global search input that enables fast searching of products and workspace module area that contains
 * quick keys.
 *
 * @module webregister.controllers.WorkspaceCtrl
 */
angular
  .module('webregister.controllers')
  .controller('WorkspaceCtrl', WorkspaceCtrl)
